import React from 'react';
import { TasticProps } from 'frontastic/tastics/types';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Button from 'components/commercetools-ui/atoms/button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Typography from 'components/commercetools-ui/atoms/typography';

const PressReleasesCoverTastic = ({ data }: TasticProps<any>) => {
  // Ensure blogs is always an array
  const blogs = Array.isArray(data?.data?.dataSource) ? data.data.dataSource : [];

  // Filter blogs with URLs containing '/press-releases/'
  const filteredBlogs = blogs.filter((blog: any) => blog.url?.includes('/product-releases/'));
  const truncateDescription = (htmlContent: string, maxLength: number = 150) => {
    // Ensure this runs only in the browser
    if (typeof window === "undefined") {
      return htmlContent; // Return original content during SSR
    }
  
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
  
    let textContent = "";
    Array.from(doc.body.childNodes).some((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        const tagName = element.tagName.toLowerCase();
  
        if (["p", "h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
          textContent = element.textContent?.trim() || "";
          return true; // Stop once valid text is found
        }
      }
      return false;
    });
  
    if (!textContent) {
      textContent = doc.body.textContent?.trim() || "";
    }
  
    return textContent.length > maxLength
      ? `${textContent.slice(0, maxLength).trim()}...`
      : textContent;
  };
  
  
  // Handle locale dynamically
  const handleLinkHover = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, 
    href: string
  ) => {
    if (typeof window !== "undefined") { 
    const domain = window.location.origin; // Get the domain dynamically
    const locale = window?.location?.pathname.split('/')[1] || 'au'; // Get the locale dynamically
    const updatedHref = `${domain}/${locale}${href.startsWith('/') ? href : `/${href}`}`;
    event.currentTarget.setAttribute('href', updatedHref);
    }
  };

  return (
    <div className="flex w-full items-center justify-between px-[20.5px] sm:px-24 lg:px-48 xl:px-60 3xl:px-80">
    <div className="w-full">
      {filteredBlogs.length > 0 ? (
        // Determine the appropriate column count dynamically
        <div
          className={`grid ${
            filteredBlogs.length >= 4
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
              : `grid-cols-1 sm:grid-cols-2 lg:grid-cols-${filteredBlogs.length}`
          } gap-10`}
        >
          {filteredBlogs.map((blog: any, index: number) => {
            const section = blog.sections?.find(
              (sec: any) => sec.section_with_image_text
            )?.section_with_image_text;
  
            if (!section) {
              return null; // Skip if no section_with_image_text is found
            }
  
            const cmsCssClass = section.additional_css || '';
            const modifiedUrl = blog.url;
            const isTitleEmpty = !section.title;
  
            return (
              <div
                key={index}
                className={`block hover:shadow-lg transition-shadow ${cmsCssClass}`}
                style={{ backgroundColor: '#F3EDE9' }}
              >
                <a
                  href={modifiedUrl}
                  className="group"
                >
                  {section.image?.url && (
                    <img
                      src={section.image.url}
                      alt={section.image.description || 'Case Study Image'}
                      className={`w-full h-auto ${cmsCssClass}`}
                    />
                  )}
                  {section.title && (
                    <div className={`p-2 mt-2 ${cmsCssClass}`}>
                      <Markdown
                        markdown={section.title}
                        className="text-lg font-semibold text-gray-800"
                      />
                    </div>
                  )}
                  {section.description && (
                    <div className={`p-2 ${cmsCssClass} ${isTitleEmpty ? 'mt-2' : ''}`}>
                      <Markdown
                        markdown={truncateDescription(section.description)}
                        className="text-sm text-gray-600"
                      />
                    </div>
                  )}
                  <div className="mt-2 text-right p-8 transform transition-transform duration-300 group-hover:translate-x-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      className="w-32 h-32 text-gray-600"
                    >
                      <path
                        fillRule="evenodd"
                        d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 01.083 1.32l-.083.094-6 6a1 1 0 01-1.497-1.32l.083-.094L17.585 12H4a1 1 0 01-.117-1.993L4 10h13.585l-4.293-4.293a1 1 0 01-.083-1.32z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </a>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-500">No product releases available.</p>
      )}
      <div className="mt-40"></div>
    </div>
  </div>  
  );
};

export default PressReleasesCoverTastic;
